import type { CompanyData, FilingData } from './types';

export const ALPHA_VANTAGE_API_KEY: string | undefined = import.meta.env
  .VITE_ALPHA_VANTAGE_API_KEY;

export const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

export const liveDataCache: Record<
  string,
  {
    price: number;
    change: number;
    changePercent: number;
    previousClose: number;
    latestTradingDay: string;
    cachedAt: number;
  }
> = {};

export const fallbackData: Record<
  string,
  {
    price: number;
    change: number;
    changePercent: number;
    previousClose: number;
    latestTradingDay: string;
  }
> = {
  BOX: {
    price: 24.31,
    change: -0.42,
    changePercent: -1.7,
    previousClose: 24.73,
    latestTradingDay: '02-05-2026',
  },
  YEXT: {
    price: 4.83,
    change: -0.16,
    changePercent: -3.21,
    previousClose: 4.99,
    latestTradingDay: '02-05-2026',
  },
  AAPL: {
    price: 264.35,
    change: -1.58,
    changePercent: -0.59,
    previousClose: 265.93,
    latestTradingDay: '02-21-2026',
  },
  MSFT: {
    price: 400.38,
    change: -3.15,
    changePercent: -0.78,
    previousClose: 403.53,
    latestTradingDay: '02-21-2026',
  },
};

// BOX Inc. data from comps file (FY ends Jan 31)
// Values from Excel in thousands
const BOX_10K_FILING_PATH = '0001372612/000095017025036261/box-20250131.htm';
const BOX_10Q_Q3_FILING_PATH = '0001372612/000119312525306665/box-20251031.htm';
const BOX_10Q_Q2_FILING_PATH = '0001372612/000095017025111701/box-20250731.htm';

const boxFilings: FilingData[] = [
  {
    id: 'box-10k-fy25',
    filingType: '10-K',
    filingDate: 'Filed Mar 2025',
    periodEnd: 'Jan 31, 2025',
    filingPath: BOX_10K_FILING_PATH,
    periods: {
      col1: 'FY 2025',
      col2: 'FY 2024',
      col3: '',
    },
    periodEndDates: {
      col1: '2025-01-31',
      col2: '2024-01-31',
      col3: '',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '1,090,130',
        col2: '1,037,741',
        col3: '',
        yoyChange: 5.0,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'Gross profit',
        col1: '862,025',
        col2: '777,129',
        col3: '',
        yoyChange: 10.9,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'Operating income',
        col1: '79,634',
        col2: '50,753',
        col3: '',
        yoyChange: 56.9,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '244,621',
        col2: '129,032',
        col3: '',
        yoyChange: 89.6,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'EPS (diluted)',
        col1: '$1.36',
        col2: '$0.67',
        col3: '',
        yoyChange: 103.0,
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '624,575',
        col2: '383,742',
        col3: '',
        yoyChange: 62.8,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
      },
      {
        label: 'Total debt',
        col1: '652,545',
        col2: '370,822',
        col3: '',
        yoyChange: 76.0,
      },
      {
        label: 'Net debt',
        col1: '27,970',
        col2: '(12,920)',
        col3: '',
        yoyChange: null,
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '332,257',
        col2: '318,727',
        col3: '',
        yoyChange: 4.2,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(2,573)',
        col2: '(4,703)',
        col3: '',
        yoyChange: -45.3,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
      {
        label: 'Non-GAAP Metrics',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'EBITDA',
        col1: '320,740',
        col2: '300,777',
        col3: '',
        yoyChange: 6.6,
      },
    ],
  },
  {
    id: 'box-10q-q3fy26',
    filingType: '10-Q',
    filingDate: 'Filed Dec 2025',
    periodEnd: 'Oct 31, 2025',
    filingPath: BOX_10Q_Q3_FILING_PATH,
    filingPathOverrides: { col2: BOX_10Q_Q2_FILING_PATH },
    periods: {
      col1: "Q3 '26",
      col2: "Q2 '26",
      col3: "Q3 '25",
    },
    periodEndDates: {
      col1: '2025-10-31',
      col2: '2025-07-31',
      col3: '2024-10-31',
    },
    quarterEnded: {
      col1: '10/31/25',
      col2: '7/31/25',
      col3: '10/31/24',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '301,107',
        col2: '293,999',
        col3: '275,913',
        yoyChange: 9.1,
        qoqChange: 2.4,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'Gross profit',
        col1: '239,532',
        col2: '232,477',
        col3: '220,357',
        yoyChange: 8.7,
        qoqChange: 3.0,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'Operating income',
        col1: '25,063',
        col2: '20,578',
        col3: '23,411',
        yoyChange: 7.1,
        qoqChange: 21.8,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '12,065',
        col2: '13,445',
        col3: '12,893',
        yoyChange: -6.4,
        qoqChange: -10.3,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'EPS (diluted)',
        col1: '$0.05',
        col2: '$0.05',
        col3: '$0.05',
        yoyChange: null,
        qoqChange: null,
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '633,159',
        col2: '657,826',
        col3: '624,575',
        yoyChange: 1.4,
        qoqChange: -3.7,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
        // Balance sheet comparative in 10-Q is prior fiscal year end, not year-ago quarter
        periodEndDateOverrides: { col3: '2025-01-31' },
      },
      {
        label: 'Total debt',
        col1: '655,177',
        col2: '654,298',
        col3: '652,545',
        yoyChange: 0.4,
        qoqChange: 0.1,
      },
      {
        label: 'Net debt',
        col1: '22,018',
        col2: '(3,528)',
        col3: '27,970',
        yoyChange: null,
        qoqChange: null,
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '73,044',
        col2: '45,964',
        col3: '62,582',
        yoyChange: 16.7,
        qoqChange: 58.9,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(1,726)',
        col2: '(2,070)',
        col3: '(271)',
        yoyChange: 536.9,
        qoqChange: -16.6,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
      {
        label: 'Non-GAAP Metrics',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'EBITDA',
        col1: '93,354',
        col2: '89,066',
        col3: '84,921',
        yoyChange: 9.9,
        qoqChange: 4.8,
      },
    ],
  },
];

const boxData: CompanyData = {
  ticker: 'BOX',
  name: 'Box, Inc.',
  sharesOutstanding: 143000, // ~143M shares (in thousands)
  filings: boxFilings,
};

// YEXT Inc. data from comps file (FY ends Jan 31)
// Values from Excel in thousands
const YEXT_10K_FILING_PATH = '0001614178/000161417825000030/yext-20250131.htm';
const YEXT_10Q_Q3_FILING_PATH =
  '0001614178/000162828025055819/yext-20251031.htm';
const YEXT_10Q_Q2_FILING_PATH =
  '0001614178/000161417825000119/yext-20250731.htm';

const yextFilings: FilingData[] = [
  {
    id: 'yext-10k-fy25',
    filingType: '10-K',
    filingDate: 'Filed Mar 2025',
    periodEnd: 'Jan 31, 2025',
    filingPath: YEXT_10K_FILING_PATH,
    periods: {
      col1: 'FY 2025',
      col2: 'FY 2024',
      col3: '',
    },
    periodEndDates: {
      col1: '2025-01-31',
      col2: '2024-01-31',
      col3: '',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '420,957',
        col2: '404,322',
        col3: '',
        yoyChange: 4.1,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'Gross profit',
        col1: '324,593',
        col2: '316,854',
        col3: '',
        yoyChange: 2.4,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'Operating income',
        col1: '(32,448)',
        col2: '(6,201)',
        col3: '',
        yoyChange: 423.3,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Acquisition-Related Costs',
        col1: '29,176',
        col2: '-',
        col3: '',
        yoyChange: null,
      },
      {
        label: 'Adjusted EBITDA',
        col1: '67,039',
        col2: '54,565',
        col3: '',
        yoyChange: 22.9,
      },
      {
        label: 'Net Income',
        col1: '(27,948)',
        col2: '(2,630)',
        col3: '',
        yoyChange: null,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'EPS (diluted)',
        col1: '($0.22)',
        col2: '($0.02)',
        col3: '',
        yoyChange: 1000.0,
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '123,133',
        col2: '210,184',
        col3: '',
        yoyChange: -41.4,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
      },
      {
        label: 'Total debt',
        col1: '-',
        col2: '-',
        col3: '',
        yoyChange: null,
      },
      {
        label: 'Net debt',
        col1: '(123,133)',
        col2: '(210,184)',
        col3: '',
        yoyChange: -41.4,
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '50,211',
        col2: '46,157',
        col3: '',
        yoyChange: 8.8,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(2,085)',
        col2: '(2,728)',
        col3: '',
        yoyChange: -23.6,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
      {
        label: 'Non-GAAP Metrics',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Adjusted EBITDA',
        col1: '67,039',
        col2: '54,565',
        col3: '',
        yoyChange: 22.9,
      },
    ],
  },
  {
    id: 'yext-10q-q3fy26',
    filingType: '10-Q',
    filingDate: 'Filed Dec 2025',
    periodEnd: 'Oct 31, 2025',
    filingPath: YEXT_10Q_Q3_FILING_PATH,
    filingPathOverrides: { col2: YEXT_10Q_Q2_FILING_PATH },
    periods: {
      col1: "Q3 '26",
      col2: "Q2 '26",
      col3: "Q3 '25",
    },
    periodEndDates: {
      col1: '2025-10-31',
      col2: '2025-07-31',
      col3: '2024-10-31',
    },
    quarterEnded: {
      col1: '10/31/25',
      col2: '7/31/25',
      col3: '10/31/24',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '111,998',
        col2: '113,094',
        col3: '113,989',
        yoyChange: -1.7,
        qoqChange: -1.0,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'Gross profit',
        col1: '82,795',
        col2: '85,034',
        col3: '87,742',
        yoyChange: -5.6,
        qoqChange: -2.6,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'Operating income',
        col1: '7,780',
        col2: '29,674',
        col3: '(10,368)',
        yoyChange: null,
        qoqChange: -73.8,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Acquisition-Related Costs',
        col1: '(4,364)',
        col2: '(23,055)',
        col3: '14,482',
        yoyChange: null,
        qoqChange: null,
      },
      {
        label: 'Adjusted EBITDA',
        col1: '22,415',
        col2: '26,369',
        col3: '23,094',
        yoyChange: -2.9,
        qoqChange: -15.0,
      },
      {
        label: 'Net Income',
        col1: '6,136',
        col2: '26,751',
        col3: '(12,799)',
        yoyChange: null,
        qoqChange: -77.1,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'EPS (diluted)',
        col1: '$0.01',
        col2: '$0.03',
        col3: '($0.10)',
        yoyChange: null,
        qoqChange: -66.7,
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '139,916',
        col2: '178,761',
        col3: '123,133',
        yoyChange: 13.6,
        qoqChange: -21.7,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
        periodEndDateOverrides: { col3: '2025-01-31' },
      },
      {
        label: 'Total debt',
        col1: '98,167',
        col2: '98,281',
        col3: '-',
        yoyChange: null,
        qoqChange: -0.1,
      },
      {
        label: 'Net debt',
        col1: '(41,749)',
        col2: '(80,480)',
        col3: '(123,133)',
        yoyChange: -66.1,
        qoqChange: -48.1,
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '(19,820)',
        col2: '8,407',
        col3: '(15,795)',
        yoyChange: 25.5,
        qoqChange: -335.8,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(515)',
        col2: '(573)',
        col3: '(577)',
        yoyChange: -10.7,
        qoqChange: -10.1,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
      {
        label: 'Non-GAAP Metrics',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Adjusted EBITDA',
        col1: '22,415',
        col2: '26,369',
        col3: '23,094',
        yoyChange: -2.9,
        qoqChange: -15.0,
      },
    ],
  },
];

const yextData: CompanyData = {
  ticker: 'YEXT',
  name: 'Yext, Inc.',
  sharesOutstanding: 122630, // ~122.63M shares (in thousands)
  filings: yextFilings,
};

// AAPL Inc. data from SEC EDGAR XBRL (FY ends ~Sep 27)
// Values in millions (Apple reports in millions)
const AAPL_10K_FILING_PATH = '0000320193/000032019325000079/aapl-20250927.htm';
const AAPL_10Q_Q1FY26_FILING_PATH =
  '0000320193/000032019326000006/aapl-20251227.htm';

const aaplFilings: FilingData[] = [
  {
    id: 'aapl-10k-fy25',
    filingType: '10-K',
    filingDate: 'Filed Oct 2025',
    periodEnd: 'Sep 27, 2025',
    filingPath: AAPL_10K_FILING_PATH,
    periods: {
      col1: 'FY 2025',
      col2: 'FY 2024',
      col3: '',
    },
    periodEndDates: {
      col1: '2025-09-27',
      col2: '2024-09-28',
      col3: '',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '416,161',
        col2: '391,035',
        col3: '',
        yoyChange: 6.4,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'COGS',
        col1: '220,960',
        col2: '210,352',
        col3: '',
        yoyChange: 5.0,
        xbrlTag: 'us-gaap:CostOfGoodsAndServicesSold',
      },
      {
        label: 'Gross profit',
        col1: '195,201',
        col2: '180,683',
        col3: '',
        yoyChange: 8.0,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'R&D',
        col1: '34,550',
        col2: '31,370',
        col3: '',
        yoyChange: 10.1,
        xbrlTag: 'us-gaap:ResearchAndDevelopmentExpense',
      },
      {
        label: 'SG&A',
        col1: '27,601',
        col2: '26,097',
        col3: '',
        yoyChange: 5.8,
        xbrlTag: 'us-gaap:SellingGeneralAndAdministrativeExpense',
      },
      {
        label: 'Operating income',
        col1: '133,050',
        col2: '123,216',
        col3: '',
        yoyChange: 8.0,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '112,010',
        col2: '93,736',
        col3: '',
        yoyChange: 19.5,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '35,934',
        col2: '29,943',
        col3: '',
        yoyChange: 20.0,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '111,482',
        col2: '118,254',
        col3: '',
        yoyChange: -5.7,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(12,715)',
        col2: '(9,447)',
        col3: '',
        yoyChange: 34.6,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
    ],
  },
  {
    id: 'aapl-10q-q1fy26',
    filingType: '10-Q',
    filingDate: 'Filed Jan 2026',
    periodEnd: 'Dec 27, 2025',
    filingPath: AAPL_10Q_Q1FY26_FILING_PATH,
    filingPathOverrides: { col2: '' },
    periods: {
      col1: "Q1 '26",
      col2: "Q4 '25",
      col3: "Q1 '25",
    },
    periodEndDates: {
      col1: '2025-12-27',
      col2: '2025-09-27',
      col3: '2024-12-28',
    },
    quarterEnded: {
      col1: '12/27/25',
      col2: '9/27/25',
      col3: '12/28/24',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '143,756',
        col2: '102,466',
        col3: '124,300',
        yoyChange: 15.7,
        qoqChange: 40.3,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'COGS',
        col1: '74,525',
        col2: '54,125',
        col3: '66,025',
        yoyChange: 12.9,
        qoqChange: 37.7,
        xbrlTag: 'us-gaap:CostOfGoodsAndServicesSold',
      },
      {
        label: 'Gross profit',
        col1: '69,231',
        col2: '48,341',
        col3: '58,275',
        yoyChange: 18.8,
        qoqChange: 43.2,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'R&D',
        col1: '10,887',
        col2: '8,866',
        col3: '8,268',
        yoyChange: 31.7,
        qoqChange: 22.8,
        xbrlTag: 'us-gaap:ResearchAndDevelopmentExpense',
      },
      {
        label: 'SG&A',
        col1: '7,492',
        col2: '7,048',
        col3: '7,175',
        yoyChange: 4.4,
        qoqChange: 6.3,
        xbrlTag: 'us-gaap:SellingGeneralAndAdministrativeExpense',
      },
      {
        label: 'Operating income',
        col1: '50,852',
        col2: '32,427',
        col3: '42,832',
        yoyChange: 18.7,
        qoqChange: 56.8,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '42,097',
        col2: '27,466',
        col3: '36,330',
        yoyChange: 15.9,
        qoqChange: 53.2,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '45,317',
        col2: '35,934',
        col3: '30,299',
        yoyChange: 49.6,
        qoqChange: 26.1,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
        periodEndDateOverrides: { col3: '2025-09-27' },
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '53,925',
        col2: '29,728',
        col3: '29,935',
        yoyChange: 80.1,
        qoqChange: 81.4,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(2,373)',
        col2: '(3,242)',
        col3: '(2,940)',
        yoyChange: -19.3,
        qoqChange: -26.8,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
    ],
  },
];

const aaplData: CompanyData = {
  ticker: 'AAPL',
  name: 'Apple Inc.',
  sharesOutstanding: 14702703, // ~14.7B shares (in thousands)
  valueScale: 'millions',
  filings: aaplFilings,
};

// MSFT data from SEC EDGAR XBRL (FY ends Jun 30)
// Values in millions (Microsoft reports in millions)
const MSFT_10K_FILING_PATH = '0000789019/000095017025100235/msft-20250630.htm';
const MSFT_10Q_Q2FY26_FILING_PATH =
  '0000789019/000119312526027207/msft-20251231.htm';
const MSFT_10Q_Q1FY26_FILING_PATH =
  '0000789019/000119312525256321/msft-20250930.htm';

const msftFilings: FilingData[] = [
  {
    id: 'msft-10k-fy25',
    filingType: '10-K',
    filingDate: 'Filed Jul 2025',
    periodEnd: 'Jun 30, 2025',
    filingPath: MSFT_10K_FILING_PATH,
    periods: {
      col1: 'FY 2025',
      col2: 'FY 2024',
      col3: '',
    },
    periodEndDates: {
      col1: '2025-06-30',
      col2: '2024-06-30',
      col3: '',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '281,724',
        col2: '245,122',
        col3: '',
        yoyChange: 14.9,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'COGS',
        col1: '87,831',
        col2: '74,114',
        col3: '',
        yoyChange: 18.5,
        xbrlTag: 'us-gaap:CostOfGoodsAndServicesSold',
      },
      {
        label: 'Gross profit',
        col1: '193,893',
        col2: '171,008',
        col3: '',
        yoyChange: 13.4,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'R&D',
        col1: '32,488',
        col2: '29,510',
        col3: '',
        yoyChange: 10.1,
        xbrlTag: 'us-gaap:ResearchAndDevelopmentExpense',
      },
      {
        label: 'Operating income',
        col1: '128,528',
        col2: '109,433',
        col3: '',
        yoyChange: 17.4,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '101,832',
        col2: '88,136',
        col3: '',
        yoyChange: 15.5,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '30,242',
        col2: '18,315',
        col3: '',
        yoyChange: 65.1,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '136,162',
        col2: '118,548',
        col3: '',
        yoyChange: 14.9,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(64,551)',
        col2: '(44,477)',
        col3: '',
        yoyChange: 45.1,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
    ],
  },
  {
    id: 'msft-10q-q2fy26',
    filingType: '10-Q',
    filingDate: 'Filed Jan 2026',
    periodEnd: 'Dec 31, 2025',
    filingPath: MSFT_10Q_Q2FY26_FILING_PATH,
    filingPathOverrides: { col2: MSFT_10Q_Q1FY26_FILING_PATH },
    periods: {
      col1: "Q2 '26",
      col2: "Q1 '26",
      col3: "Q2 '25",
    },
    periodEndDates: {
      col1: '2025-12-31',
      col2: '2025-09-30',
      col3: '2024-12-31',
    },
    quarterEnded: {
      col1: '12/31/25',
      col2: '9/30/25',
      col3: '12/31/24',
    },
    rows: [
      {
        label: 'Income Statement',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Revenue',
        col1: '81,273',
        col2: '77,673',
        col3: '69,632',
        yoyChange: 16.7,
        qoqChange: 4.6,
        xbrlTag: 'us-gaap:RevenueFromContractWithCustomerExcludingAssessedTax',
      },
      {
        label: 'COGS',
        col1: '25,978',
        col2: '24,043',
        col3: '21,799',
        yoyChange: 19.2,
        qoqChange: 8.1,
        xbrlTag: 'us-gaap:CostOfGoodsAndServicesSold',
      },
      {
        label: 'Gross profit',
        col1: '55,295',
        col2: '53,630',
        col3: '47,833',
        yoyChange: 15.6,
        qoqChange: 3.1,
        xbrlTag: 'us-gaap:GrossProfit',
      },
      {
        label: 'R&D',
        col1: '8,504',
        col2: '8,146',
        col3: '7,917',
        yoyChange: 7.4,
        qoqChange: 4.4,
        xbrlTag: 'us-gaap:ResearchAndDevelopmentExpense',
      },
      {
        label: 'Operating income',
        col1: '38,275',
        col2: '37,961',
        col3: '31,653',
        yoyChange: 20.9,
        qoqChange: 0.8,
        xbrlTag: 'us-gaap:OperatingIncomeLoss',
      },
      {
        label: 'Net Income',
        col1: '38,458',
        col2: '27,747',
        col3: '24,108',
        yoyChange: 59.5,
        qoqChange: 38.6,
        xbrlTag: 'us-gaap:NetIncomeLoss',
      },
      {
        label: 'Balance Sheet',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Cash and cash equivalents',
        col1: '24,296',
        col2: '28,849',
        col3: '30,242',
        yoyChange: -19.7,
        qoqChange: -15.8,
        xbrlTag: 'us-gaap:CashAndCashEquivalentsAtCarryingValue',
        periodEndDateOverrides: { col3: '2025-06-30' },
      },
      {
        label: 'Cash Flow',
        col1: '',
        col2: '',
        col3: '',
        yoyChange: null,
        qoqChange: null,
        isHeader: true,
      },
      {
        label: 'Operating CF',
        col1: '35,758',
        col2: '45,057',
        col3: '22,291',
        yoyChange: 60.4,
        qoqChange: -20.6,
        xbrlTag: 'us-gaap:NetCashProvidedByUsedInOperatingActivities',
      },
      {
        label: 'CapEx',
        col1: '(29,876)',
        col2: '(19,394)',
        col3: '(15,804)',
        yoyChange: 89.0,
        qoqChange: 54.0,
        xbrlTag: 'us-gaap:PaymentsToAcquirePropertyPlantAndEquipment',
      },
    ],
  },
];

const msftData: CompanyData = {
  ticker: 'MSFT',
  name: 'Microsoft Corporation',
  sharesOutstanding: 7429000, // ~7.4B shares (in thousands)
  valueScale: 'millions',
  filings: msftFilings,
};

export const companyDataMap: Record<string, CompanyData> = {
  BOX: boxData,
  YEXT: yextData,
  AAPL: aaplData,
  MSFT: msftData,
};

const defaultFiling: FilingData = {
  id: 'unknown',
  filingType: '10-Q',
  filingDate: '',
  periodEnd: '',
  filingPath: '',
  periods: {
    col1: 'Period 1',
    col2: 'Period 2',
    col3: 'Period 3',
  },
  periodEndDates: {
    col1: '',
    col2: '',
    col3: '',
  },
  rows: [],
};

export const defaultData: CompanyData = {
  ticker: '???',
  name: 'Unknown Company',
  sharesOutstanding: 0,
  filings: [defaultFiling],
};

export const defaultFilingInfo: Record<
  string,
  { filingType: string; year: string }
> = {
  BOX: { filingType: '10-K', year: '2025' },
  YEXT: { filingType: '10-K', year: '2025' },
  AAPL: { filingType: '10-K', year: '2025' },
  MSFT: { filingType: '10-K', year: '2025' },
};

export const filingPathToInfo: Record<
  string,
  { ticker: string; filingType: string; year: string }
> = {
  // BOX filings
  '000095017025036261': { ticker: 'BOX', filingType: '10-K', year: '2025' }, // FY25 (Jan 31, 2025)
  '000119312525306665': { ticker: 'BOX', filingType: '10-Q', year: 'Q3 2026' }, // Q3 FY26 (Oct 31, 2025)
  '000095017025111701': { ticker: 'BOX', filingType: '10-Q', year: 'Q2 2026' }, // Q2 FY26 (Jul 31, 2025)
  // YEXT filings
  '000161417825000030': { ticker: 'YEXT', filingType: '10-K', year: '2025' }, // FY25 (Jan 31, 2025)
  '000162828025055819': { ticker: 'YEXT', filingType: '10-Q', year: 'Q3 2026' }, // Q3 FY26 (Oct 31, 2025)
  '000161417825000119': { ticker: 'YEXT', filingType: '10-Q', year: 'Q2 2026' }, // Q2 FY26 (Jul 31, 2025)
  // AAPL filings
  '000032019325000079': { ticker: 'AAPL', filingType: '10-K', year: '2025' }, // FY25 (Sep 27, 2025)
  '000032019326000006': { ticker: 'AAPL', filingType: '10-Q', year: 'Q1 2026' }, // Q1 FY26 (Dec 27, 2025)
  // MSFT filings
  '000095017025100235': { ticker: 'MSFT', filingType: '10-K', year: '2025' }, // FY25 (Jun 30, 2025)
  '000119312526027207': { ticker: 'MSFT', filingType: '10-Q', year: 'Q2 2026' }, // Q2 FY26 (Dec 31, 2025)
  '000119312525256321': { ticker: 'MSFT', filingType: '10-Q', year: 'Q1 2026' }, // Q1 FY26 (Sep 30, 2025)
};

export function getFilingInfoFromPath(
  urlPath: string
): { ticker: string; filingType: string; year: string } | null {
  for (const [accession, info] of Object.entries(filingPathToInfo)) {
    if (urlPath.includes(accession)) {
      return info;
    }
  }
  return null;
}
